import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Plus,
  List,
  LogOut,
  Home,
  MessageSquare,
  Star,
  Menu,
  X,
} from "lucide-react";
import { useContactStats } from "@/lib/hooks";
import { useLanguage } from "@/lib/language";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface AdminNavigationProps {
  currentView: "list" | "create" | "edit" | "contacts" | "testimonials";
  onNavigate: (
    view: "list" | "create" | "edit" | "contacts" | "testimonials"
  ) => void;
  onLogout: () => void;
  onCreateNew: () => void;
}

export function AdminNavigation({
  currentView,
  onNavigate,
  onLogout,
  onCreateNew,
}: AdminNavigationProps) {
  const { data: contactStats } = useContactStats();
  const { t, dir } = useLanguage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigationItems = [
    {
      key: "list" as const,
      icon: List,
      label: t("properties"),
      onClick: () => onNavigate("list"),
    },
    {
      key: "contacts" as const,
      icon: MessageSquare,
      label: t("contacts"),
      onClick: () => onNavigate("contacts"),
      badge: contactStats?.unread_contacts,
    },
    {
      key: "testimonials" as const,
      icon: Star,
      label: t("testimonials"),
      onClick: () => onNavigate("testimonials"),
    },
  ];

  const handleMobileNavClick = (action: () => void) => {
    action();
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo/Title */}
          <div className="flex items-center space-x-2 md:space-x-4">
            <div className="text-lg md:text-xl font-bold text-gray-900">
              {t("realEstateAdmin")}
            </div>
            <Badge variant="secondary" className="hidden sm:inline-flex">
              {t("adminPanel")}
            </Badge>
          </div>

          {/* Desktop Navigation - Hidden on mobile */}
          <div className="hidden lg:flex items-center space-x-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Button
                  key={item.key}
                  variant={currentView === item.key ? "default" : "ghost"}
                  size="sm"
                  onClick={item.onClick}
                  className="flex items-center space-x-2"
                >
                  <Icon className="h-4 w-4" />
                  <span>{item.label}</span>
                  {item.badge && item.badge > 0 && (
                    <Badge
                      variant="destructive"
                      className="ml-1 h-5 w-5 p-0 text-xs"
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Button>
              );
            })}

            <Button
              variant="outline"
              size="sm"
              onClick={onCreateNew}
              className="flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>{t("addProperty")}</span>
            </Button>

            <div className="h-4 w-px bg-gray-300 mx-2" />

            <LanguageSwitcher />

            <Button
              variant="ghost"
              size="sm"
              onClick={() => window.open("/", "_blank")}
              className="flex items-center space-x-2"
            >
              <Home className="h-4 w-4" />
              <span>{t("viewSite")}</span>
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              className="flex items-center space-x-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" />
              <span>{t("logout")}</span>
            </Button>
          </div>

          {/* Mobile Navigation */}
          <div className="flex lg:hidden items-center space-x-2">
            <LanguageSwitcher />
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" className="p-2">
                  <Menu className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent
                side={dir === "rtl" ? "right" : "left"}
                className={`w-[300px] sm:w-[400px] ${
                  dir === "rtl"
                    ? "[&>button]:left-4 [&>button]:right-auto"
                    : "[&>button]:right-4 [&>button]:left-auto"
                }`}
                dir={dir}
              >
                <SheetHeader>
                  <SheetTitle>{t("adminPanel")}</SheetTitle>
                </SheetHeader>
                <div className="flex flex-col px-2">
                  {navigationItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Button
                        key={item.key}
                        variant={currentView === item.key ? "default" : "ghost"}
                        onClick={() => handleMobileNavClick(item.onClick)}
                        className="flex items-center justify-start space-x-3 w-full"
                      >
                        <Icon className="h-5 w-5" />
                        <span>{item.label}</span>
                        {item.badge && item.badge > 0 && (
                          <Badge
                            variant="destructive"
                            className="ml-auto h-5 w-5 p-0 text-xs"
                          >
                            {item.badge}
                          </Badge>
                        )}
                      </Button>
                    );
                  })}

                  <div className="border-t pt-4">
                    <Button
                      variant="outline"
                      onClick={() => handleMobileNavClick(onCreateNew)}
                      className="flex items-center justify-start space-x-3 w-full"
                    >
                      <Plus className="h-5 w-5" />
                      <span>{t("addProperty")}</span>
                    </Button>
                  </div>

                  <div className="border-t pt-4">
                    <Button
                      variant="ghost"
                      onClick={() =>
                        handleMobileNavClick(() => window.open("/", "_blank"))
                      }
                      className="flex items-center justify-start space-x-3 w-full"
                    >
                      <Home className="h-5 w-5" />
                      <span>{t("viewSite")}</span>
                    </Button>

                    <Button
                      variant="ghost"
                      onClick={() => handleMobileNavClick(onLogout)}
                      className="flex items-center justify-start space-x-3 w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <LogOut className="h-5 w-5" />
                      <span>{t("logout")}</span>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
}
